'use client'

import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from '@/utils/styles/cn'
import { Cross2Icon } from '@radix-ui/react-icons'
import { motion } from 'framer-motion'
import { KeybindingModuleProvider, useKeybindingModuleContext } from '@/features/keybindings/keybinding-module-context'
import { useMediaQuery } from '@/utils/hooks/use-media-query'
import { RequireSome } from '@/utils/typings/require-some'
import { SuperscaleLogoIcon } from '@/components/icons/superscale-logo-icon'

/** When true, the dialog is rendering the mobile full-screen header row; StepHeader should not duplicate it. */
export const MobileHeaderRenderedByDialogContext = React.createContext(false)

/** Single source of truth for mobile full-screen dialog header row (logo/close or logo/org switcher). Use in both Dialog and StepHeader so spacing to title is identical. */
export const MOBILE_FULLSCREEN_HEADER_ROW_CLASS =
    'flex w-full min-h-[34px] items-center justify-between gap-2 px-4 mb-2 md:hidden'

const Dialog: React.FC<
    RequireSome<DialogPrimitive.DialogProps, 'open' | 'onOpenChange'> & { isBillingBlocker?: boolean }
> = ({ onOpenChange: propsOnOpenChange, open: rawOpen, ...props }) => {
    const { setActive: setUpperModuleActive } = useKeybindingModuleContext()

    const onOpenChange = React.useCallback(
        (nextOpen: boolean) => {
            // Disable keybindings above when the dialog is open
            if (!nextOpen) setUpperModuleActive()

            propsOnOpenChange(nextOpen)
        },
        [propsOnOpenChange, setUpperModuleActive]
    )

    const open = rawOpen

    const close = React.useCallback(() => {
        propsOnOpenChange(false)
    }, [propsOnOpenChange])

    const modalRoot = (
        <DialogContext.Provider value={{ close, open }}>
            <DialogPrimitive.Root onOpenChange={onOpenChange} open={open} {...props} />
        </DialogContext.Provider>
    )

    switch (open) {
        case true:
            return <KeybindingModuleProvider>{modalRoot}</KeybindingModuleProvider>

        case false:
            return modalRoot
    }
}

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

type IDialogOverlayProps = React.HTMLAttributes<HTMLDivElement> & {
    overlayClassName?: string
    overlayStyle?: React.CSSProperties
    /** When false, no fade animation. Default true. */
    animated?: boolean
    transition?: { duration: number }
}

export const DialogOverlay = React.forwardRef<HTMLDivElement, IDialogOverlayProps>(
    ({ className, overlayClassName, overlayStyle, animated = true, transition, onClick, children, ...props }, ref) => {
        const baseClass = cn(
            'fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto overscroll-contain',
            overlayClassName,
            className
        )
        if (animated) {
            return (
                <motion.div
                    ref={ref}
                    className={baseClass}
                    style={overlayStyle}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={transition ?? { duration: 0.2 }}
                    onClick={onClick}
                >
                    {children}
                </motion.div>
            )
        }
        return (
            <div ref={ref} className={baseClass} style={overlayStyle} aria-hidden onClick={onClick} {...props}>
                {children}
            </div>
        )
    }
)
DialogOverlay.displayName = 'DialogOverlay'

type IDialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    withoutCloseButton?: boolean
    overlayClassName?: string
    overlayContent?: React.ReactNode
    /** Allow interactions outside dialog content while open. */
    disableOutsidePointerEvents?: boolean
    /** On viewports below md, make the dialog full screen (inset-0, no radius) for less distraction. */
    fullScreenOnMobile?: boolean
    /** When true and fullScreenOnMobile, do not show the logo in the dialog chrome (e.g. when step header already shows it). */
    hideMobileLogo?: boolean
}

function isCookieBannerClick(event: { target: EventTarget | null }) {
    const target = event.target
    if (!(target instanceof HTMLElement)) return false
    return !!target.closest('[class*="cky-"], [id*="cky-"], [data-cky-tag]')
}

function isAllowedOutsideInteractionTarget(event: { target: EventTarget | null }) {
    const target = event.target
    if (!(target instanceof HTMLElement)) return false
    return !!target.closest('[data-dialog-allow-interaction="true"]')
}

const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, IDialogContentProps>(
    (
        {
            className,
            children,
            withoutCloseButton = false,
            overlayClassName,
            overlayContent,
            fullScreenOnMobile = false,
            hideMobileLogo = false,
            onPointerDownOutside,
            onInteractOutside,
            ...props
        },
        ref
    ) => {
        const isMobileViewport = useMediaQuery('(max-width: 767px)')

        const fullScreenOverlayStyle: React.CSSProperties | undefined =
            fullScreenOnMobile && isMobileViewport
                ? {
                      position: 'fixed',
                      inset: 0,
                      left: 0,
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '100%',
                      minWidth: '100%',
                      maxWidth: '100%',
                  }
                : undefined

        const fullScreenContentStyle: React.CSSProperties | undefined =
            fullScreenOnMobile && isMobileViewport
                ? {
                      position: 'fixed',
                      inset: 0,
                      left: 0,
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '100%',
                      minWidth: '100%',
                      maxWidth: '100%',
                  }
                : undefined

        const isMobileFullScreen = fullScreenOnMobile && isMobileViewport

        const classNames = React.useMemo(
            () =>
                cn(
                    'fixed left-[50%] top-[50%] z-50 overflow-hidden z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border bg-white p-6 shadow-lg rounded-[12px] focus:outline-hidden focus:ring-0',
                    fullScreenOnMobile &&
                        'max-md:fixed max-md:inset-0 max-md:translate-x-0 max-md:translate-y-0 max-md:max-w-none max-md:max-h-none max-md:rounded-none max-md:flex max-md:flex-col max-md:items-stretch max-md:justify-start max-md:min-h-0 max-md:gap-0 max-md:overflow-y-auto max-md:overscroll-contain',
                    className,
                    fullScreenOnMobile && 'max-md:!pt-4'
                ),
            [className, fullScreenOnMobile]
        )

        const renderDialogMobileHeader = fullScreenOnMobile && (!hideMobileLogo || !withoutCloseButton)

        return (
            <DialogPortal>
                <DialogPrimitive.Overlay asChild>
                    <DialogOverlay
                        overlayClassName={cn(
                            fullScreenOnMobile &&
                                'max-md:overflow-hidden max-md:overscroll-none max-md:items-stretch max-md:justify-start',
                            overlayClassName
                        )}
                        overlayStyle={fullScreenOverlayStyle}
                        transition={{ duration: isMobileFullScreen ? 0.1 : 0.2 }}
                    >
                        {overlayContent}
                    </DialogOverlay>
                </DialogPrimitive.Overlay>

                <DialogPrimitive.Content
                    asChild
                    onPointerDownOutside={(e) => {
                        if (isCookieBannerClick(e)) {
                            e.preventDefault()
                            return
                        }
                        if (isAllowedOutsideInteractionTarget(e)) return
                        onPointerDownOutside?.(e)
                    }}
                    onInteractOutside={(e) => {
                        if (isCookieBannerClick(e) || isAllowedOutsideInteractionTarget(e)) {
                            e.preventDefault()
                            return
                        }
                        onInteractOutside?.(e)
                    }}
                    {...props}
                >
                    <motion.div
                        ref={ref}
                        className={classNames}
                        style={fullScreenContentStyle}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ opacity: { duration: isMobileFullScreen ? 0 : 0.15 } }}
                    >
                        {renderDialogMobileHeader ? (
                            <MobileHeaderRenderedByDialogContext.Provider value={true}>
                                <div className={MOBILE_FULLSCREEN_HEADER_ROW_CLASS}>
                                    <div className="min-w-0 flex items-center">
                                        {!hideMobileLogo && (
                                            <div className="flex items-center gap-1">
                                                <SuperscaleLogoIcon className="size-5" />
                                                <span className="text-sm font-semibold">Superscale</span>
                                            </div>
                                        )}
                                    </div>
                                    {!withoutCloseButton && (
                                        <div className="shrink-0">
                                            <DialogCloseButton placement="inline" />
                                        </div>
                                    )}
                                </div>
                                {children}
                            </MobileHeaderRenderedByDialogContext.Provider>
                        ) : (
                            children
                        )}
                        {!withoutCloseButton &&
                            (fullScreenOnMobile ? (
                                <div className="max-md:hidden">
                                    <DialogCloseButton />
                                </div>
                            ) : (
                                <DialogCloseButton />
                            ))}
                    </motion.div>
                </DialogPrimitive.Content>
            </DialogPortal>
        )
    }
)
DialogContent.displayName = DialogPrimitive.Content.displayName

export const DialogCloseButton = ({
    xClassName,
    placement = 'absolute',
    className,
}: {
    xClassName?: string
    placement?: 'absolute' | 'inline'
    className?: string
}) => {
    return (
        <DialogPrimitive.Close
            className={cn(
                'ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground p-2 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none z-10',
                (placement === 'absolute' || className) && 'absolute',
                placement === 'absolute' && !className && 'right-2 top-2.5 md:right-3 md:top-3.5',
                placement === 'inline' && '-mr-2 -mt-2',
                className
            )}
        >
            <Cross2Icon className={cn('h-5 w-5', xClassName)} />
            <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
    )
}

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn('text-lg font-semibold leading-none tracking-tight', className)}
        {...props}
    />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Description ref={ref} className={cn('text-muted-foreground text-sm', className)} {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
    Dialog,
    DialogPortal,
    DialogTrigger,
    DialogClose,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
}

export const LargeDialogContent = React.forwardRef<HTMLDivElement, IDialogContentProps>(
    ({ className, style, ...props }, ref) => {
        const isMobileViewport = useMediaQuery('(max-width: 767px)')
        const sizingStyle: React.CSSProperties = isMobileViewport
            ? { width: '100vw', minWidth: '100vw', height: '100dvh', minHeight: '100dvh' }
            : { width: '90vw', minWidth: '90vw', height: '90vh', minHeight: '90vh' }
        return (
            <DialogContent
                className={cn(
                    'flex w-full flex-col overflow-y-scroll overflow-x-hidden force-rounded-corners',
                    !props.withoutCloseButton && 'pt-8 pr-8',
                    'max-md:rounded-none!',
                    className
                )}
                style={{
                    ...sizingStyle,
                    margin: 'auto',
                    transformOrigin: 'center',
                    ...style,
                }}
                ref={ref}
                {...props}
            />
        )
    }
)

LargeDialogContent.displayName = 'LargeDialogContent'

interface IDialogContext {
    close: () => void
    open: boolean
}

const DialogContext = React.createContext<IDialogContext | null>(null)

export const useDialogContext = () => React.useContext(DialogContext)
